import { fabric } from 'fabric'
import type { FrameSize } from '../frame'
import WbFrame, { frameSizes } from '../frame'
import WbArticleImage from '../articleImage'
import WbImage from '../image'
import WbModelDetails from '../modelDetails'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, whiteboardConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import utils from '@/services/utils'
import graphicTeeTechSheetImage from '@/assets/templateLayoutImages/graphicTeeTechSheet.png'

class GraphicTeeTechSheet implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.graphicTeeTechSheet
  name = 'Graphic Tee Tech Sheet'
  layoutImage = graphicTeeTechSheetImage
  imgSize: number = 300
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  frameWidth: number = frameSizes.wide.width
  frameHeight: number = frameSizes.wide.height
  logoWidth = 155
  logoHeight = 87
  logoScaling = 20
  uaNameLogoWidth = 400
  uaNameLogoHeight = 38
  uaNameLogoScaling = 40
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  frameSpacing = 50
  imageDefaultScaleFactor: number = whiteboardConstants.frameImageDefaultScaleFactors.large
  leftPadding: number = 50
  rightPadding: number = 30
  textObjectsMaxWidth = 200
  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    bottomPadding: 30,
    fontSize: 34,
    width: 325,
    fontWeight: 'bold',
  }

  modelDetailsProps = {
    padding: 17,
    fontSize: 12,
    width: 325,
  }

  headerImageNameLog = {
    bottomPadding: 180,
  }

  getOptions(): IWhiteboardTemplateOption[] {
    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // return [{
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // }]

    return []
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, startPosition: IPoint, excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, retailPg, wholesalePg, outletPg, isExporting)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    this.imgSize = (this.imageWidth * this.imageDefaultScaleFactor) / 100
    this.startPositionX = isExporting ? 0 : startPosition.x ? startPosition.x : 0
    this.startPositionY = isExporting ? 0 : startPosition.y ? startPosition.y : 0
    const frameSize = 'wide' // options.frameSize
    this.changePropsAsPerFrameSize(frameSize)

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let frameLeft = this.startPositionX
    const frameTop = this.startPositionY
    let frameCount = 0

    for (const firstArticle of uniqueModelArticles) {
      const frameObjects: IWbObject[] = []

      frameObjects.push(await this.createHeaderLogo(
        frameTop + this.leftPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      frameObjects.push(await this.createHeaderNameLogo(
        frameTop + this.frameHeight - this.headerImageNameLog.bottomPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      const imgLeft = frameLeft + this.frameWidth - this.imgSize - this.rightPadding
      const imgTop = frameTop + this.frameHeight - this.imgSize - this.articleImageProps.bottomPadding
      frameObjects.push(await this.createImage(
        imgTop,
        imgLeft,
        firstArticle,
        this.imageDefaultScaleFactor,
        isExporting,
      ))

      const modelNameOpt = {
        left: frameLeft + this.frameWidth - this.modelNameProps.width,
        top: frameTop + this.frameHeight / 2,
        showLabels: false,
        width: this.modelNameProps.width,
        attributes: ['ModelName'],
        attributePlaceHolder: '[Blank]',
        fontFamily: this.font,
        fontSize: this.modelNameProps.fontSize,
        fontWeight: 'bold',
        textAlign: 'left',
        lock: false,
        fill: '#ffffff',
      }
      const modelNameObject = await WbModelDetails.loadModelDetails(firstArticle, modelNameOpt)
      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, this.modelNameProps.width, this.font, this.modelNameProps.fontSize, this.modelNameProps.fontWeight)
      const modelNameTop = frameTop + this.frameHeight / 2 - modelNameHeight - this.modelNameProps.bottomPadding
      modelNameObject.top = modelNameTop
      frameObjects.push(isExporting ? modelNameObject.toObject() as WbModelDetails : modelNameObject)

      const modelDetailsTop = (modelNameObject && modelNameObject.top && modelNameObject.height ? modelNameObject.top + modelNameObject.height : frameTop + this.frameHeight / 2) + this.modelDetailsProps.padding
      const modelDetailsOpt = {
        left: frameLeft + this.frameWidth - this.modelDetailsProps.width,
        top: modelDetailsTop,
        showLabels: false,
        width: this.modelDetailsProps.width,
        attributes: ['ModelNumber', '_RetailPrice'],
        attributesSeparator: ' // ',
        attributePlaceHolder: '[Blank]',
        fontFamily: this.font,
        fontSize: this.modelDetailsProps.fontSize,
        fontWeight: 'bold',
        textAlign: 'left',
        lock: false,
        fill: '#ffffff',
      }
      const modelDetailsObject = await WbModelDetails.loadModelDetails(firstArticle, modelDetailsOpt)
      frameObjects.push(isExporting ? modelDetailsObject.toObject() as WbModelDetails : modelDetailsObject)

      objects.push(...frameObjects)
      objects.push(new WbFrame(
        frameSize,
        {
          children: frameObjects.map(obj => obj.id || ''),
          name: firstArticle.ModelName,
          left: frameLeft,
          top: frameTop,
          sortOrder: frameCount,
          lock: false,
          fill: '#000000',
        },
      ))

      frameLeft += isExporting ? 0 : this.frameWidth + this.frameSpacing
      frameCount++
    }

    return objects
  }

  changePropsAsPerFrameSize(frameSize: FrameSize) {
    this.frameWidth = frameSizes[frameSize].width
    this.frameHeight = frameSizes[frameSize].height
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async createHeaderNameLogo(top: number, left: number, isExporting: boolean = false) {
    const value = { scale: this.uaNameLogoScaling }
    const options = {
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaNameLogoWhite, value, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaNameLogoWhite, options).then((newImg) => {
        newImg.setProp('scale', value)
        return newImg
      })
    }
  }

  async createHeaderLogo(top: number, left: number, isExporting: boolean = false) {
    const value = { scale: this.logoScaling }
    const options = {
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaIconWhite, value, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaIconWhite, options).then((newImg) => {
        newImg.setProp('scale', value)
        return newImg
      })
    }
  }

  async createImage(top: number, left: number, article: MyArticle, imageScaleFactor: number, isExporting: boolean) {
    const scaleValue = { scale: imageScaleFactor }
    if (isExporting) {
      return await WbArticleImage.getJsonObject(article, 500, 500, scaleValue, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle })
    }
    else {
      return await WbArticleImage.loadArticleImage(article, 500, 500, { top, left, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle }).then((newImg) => {
        newImg.setProp('scale', scaleValue)
        return newImg
      })
    }
  }
}

export default new GraphicTeeTechSheet()
